import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import {
  Heart, Church, UserCircle, Settings, HelpCircle, Shield, LogOut,
} from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";

/**
 * Top navigation bar shared across the Dashboard.
 *
 * Owns no state itself — the parent passes in handlers + dialog openers.
 * Kept stateless on purpose so it can be reused on other authenticated screens.
 */
export function DashboardNav({ user, onProfile, onHelp, onLogout }) {
  const showAdmin = user?.is_admin || (user?.church_admin_ids?.length > 0);
  return (
    <nav className="bg-white border-b border-stone px-4 py-3 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-logo">
          <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
            <Heart className="w-4 h-4 text-white" />
          </div>
          <span className="font-serif font-semibold text-lg text-stone-dark">Prayer Tracker</span>
        </Link>

        <div className="flex items-center gap-3">
          <Link
            to="/churches"
            className="flex items-center gap-1.5 text-sm text-stone-dark/60 hover:text-sage transition-colors"
            data-testid="churches-link"
          >
            <Church className="w-4 h-4" />
            <span className="hidden sm:inline">Churches</span>
          </Link>
          <Link
            to="/donate"
            className="flex items-center gap-1.5 text-sm text-stone-dark/60 hover:text-sage transition-colors"
            data-testid="donate-link"
          >
            <Heart className="w-4 h-4" />
            <span className="hidden sm:inline">Donate</span>
          </Link>

          <ThemeToggle />
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <UserCircle className="w-5 h-5 text-stone-dark/70" />
                <span className="hidden sm:inline text-stone-dark">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-stone-dark/70">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onProfile} data-testid="profile-btn">
                <Settings className="w-4 h-4 mr-2" /> Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onHelp} data-testid="help-btn">
                <HelpCircle className="w-4 h-4 mr-2" /> Help &amp; Support
              </DropdownMenuItem>
              {showAdmin && (
                <DropdownMenuItem asChild data-testid="admin-link">
                  <Link to="/admin">
                    <Shield className="w-4 h-4 mr-2" />
                    {user.is_admin ? "Admin Panel" : "Manage My Church"}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
